import { ControlOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Flex, Form, Input, Modal, Select, Space, Splitter, Table, Tag, Tooltip } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelectorSize } from '../../hooks/useWindowSize';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { KPI_CODE } from '../../enum/toshiba.enum';
import { AppContext } from '../../contexts/AppContext';
import APIList from '../../http/ApiList';
import { API_MODE, getS3Object } from '@meteor/frontend-core';
import { convertToJSON } from '../../utils/commonUtil';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { NAME_PATH, useTralitSetting } from '../../hooks/useTralitSetting';
import { EVALUATION_PERIOD } from '../../enum/common.enum';
import { buSortMap, segmentSortSeqMap } from '../../contexts/provider/toshiba.provider';
import { Card } from 'antd';
import { groupBy, isNil, orderBy, sortBy, sumBy } from 'lodash';
import { useSnapshot } from '../../hooks/useSnapshot';
import TicketHeader from './ticket-header';
import { useSessionStorageState } from 'ahooks';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const dateRange = (c: { monthly: string }, filter: any, prevYear = false) => {
  if (filter.aggregationPeriod) {
    const [startDate, endDate] = filter.aggregationPeriod;
    if (prevYear) {
      const curDate = dayjs(c.monthly).add(1, 'year');
      return curDate.isSameOrAfter(startDate) && curDate.isSameOrBefore(endDate);
    }
    return dayjs(c.monthly).isSameOrAfter(startDate) && dayjs(c.monthly).isSameOrBefore(endDate);
  }
  return true;
};

const drillLabelsMap = {
  segment: 'セグメント',
  businessUnit: 'BU',
  model: '機種',
  customer: '顧客名',
  location: '設置場所名',
};

const balanceKeyMap = {
  [EVALUATION_PERIOD.ACTUAL_ONLY]: 'salesActuals',
  [EVALUATION_PERIOD.FORECAST_ONLY]: 'salesForecast',
  [EVALUATION_PERIOD.FULL_PERIOD]: 'full',
};

type ChartProps = {};

const TicketPage: React.FC<ChartProps> = (props) => {
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const { color, getCalenderMap } = useContext(AppContext);
  const { selectorHeight4Table } = useSelectorSize({
    selector: '.project-table .tralit-table-tbody',
    watcherSelector: ['.tralit-splitter-panel'],
  });
  const [loading, setLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams);

  const { currentSnapshot, prefSnapshot, getMonthLabel } = useSnapshot();

  /**
   * 通过日历获取集計期間
   */
  const getAggregationPeriod = useCallback(
    (snapshot, fiscalYear, evaluationPeriod) => {
      const calenderMap = getCalenderMap(snapshot);
      if (calenderMap && calenderMap[fiscalYear]) {
        return calenderMap[fiscalYear][evaluationPeriod].period;
      }
      return null;
    },
    [getCalenderMap]
  );

  const [form] = Form.useForm();

  const { kpiValue, kpiUnitLabel, drillFields, setDrillFields, filter, setFilter } = useContext(ToshibaContext);

  const defaultDrillDownRule = useMemo(() => ['segment', 'businessUnit', 'model', 'customer', 'location'], []);
  const [drillDownRule, setDrillDownRule] = useSessionStorageState<any>('ticketDrillDown', {
    defaultValue: defaultDrillDownRule,
  });

  const curDrillFieldKey = useMemo(() => {
    const lastDrillField = drillFields.at(-1);
    if (lastDrillField) {
      if (drillDownRule.length === drillDownRule.indexOf(lastDrillField.fieldKey)) {
        return drillDownRule.at(-1);
      }
      return drillDownRule[drillDownRule.indexOf(lastDrillField.fieldKey) + 1];
    } else {
      return drillDownRule[0];
    }
  }, [drillFields]);
  const [curData, setCurData] = useState([]);
  const [tarData, setTarData] = useState([]);
  const [planData, setPlanData] = useState([]);
  const [lastYearData, setLastYearData] = useState([]);
  const [overallCur, setOverallCur] = useState([]);
  const [overallTar, setOverallTar] = useState([]);
  const [overallLY, setOverallLY] = useState([]);
  const [projectData, setProjectData] = useState([]);

  const getData = ({ targetSnapshot, fiscalYear, evaluationPeriod }) => {
    if (!currentSnapshot || !targetSnapshot) {
      return;
    }
    setLoading(true);
    // 计算集計期間
    const aggregationPeriod = getAggregationPeriod(currentSnapshot, 'FY25', evaluationPeriod);
    if (aggregationPeriod) {
      const [startDate, endDate] = aggregationPeriod;
      APIList.getTicketData()
        .post({
          currentSnapshot,
          targetSnapshot,
          apiMode: API_MODE.LONG_RESPONSE,
          fiscalYear,
          aggregationPeriod: [startDate.format('YYYYMM'), endDate.format('YYYYMM')],
        })
        .then(async (res) => {
          const data = await getS3Object(process.env.REACT_APP_BUCKET_NAME_TOSHIBA, res.bucketKey);
          const cur = convertToJSON(data.currentData);
          const plan = convertToJSON(data.planData);
          const tar = convertToJSON(data.targetData);
          const lastYear = convertToJSON(data.lastYearData);
          const overallCur = convertToJSON(data.overallCur);
          const overallTar = convertToJSON(data.overallTar);
          const overallLY = convertToJSON(data.overallLY);
          const projectData = convertToJSON(data.projectData);
          setCurData(cur);
          setPlanData(plan);
          setTarData(tar);
          setLastYearData(lastYear);
          setOverallCur(overallCur);
          setOverallTar(overallTar);
          setOverallLY(overallLY);
          setProjectData(projectData);

          const aggregationPeriod = getAggregationPeriod(currentSnapshot, 'FY25', evaluationPeriod);
          const filter = {
            targetSnapshot,
            fiscalYear,
            evaluationPeriod,
            aggregationPeriod,
          };
          setFilter(filter);
          form.setFieldsValue(filter);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentSnapshot) {
      if (drillFields.findIndex((item) => item.fieldKey === 'orderStatus') > 1) {
        const [segment, businessUnit] = drillFields;
        setDrillFields([segment, businessUnit]);
      }
      getData({
        fiscalYear: 'FY25',
        evaluationPeriod: params.evaluationPeriod || filter.evaluationPeriod || EVALUATION_PERIOD.FULL_PERIOD,
        targetSnapshot: filter.targetSnapshot || prefSnapshot,
      });
    }
  }, [currentSnapshot, prefSnapshot]);

  const filterMethod = useCallback(
    ({ onlyDrill = false, excludeAJ = false }) =>
      (item) => {
        if (onlyDrill) {
          return drillFields.every((drillField) => drillField.value.includes(item[drillField.fieldKey]));
        } else {
          if (filter.aggregationPeriod) {
            return (
              (excludeAJ ? item.projectName !== '調整' : true) &&
              drillFields.every((drillField) => drillField.value.includes(item[drillField.fieldKey]))
              //  &&  dateRange(item, filter, prefYear)
            );
          }
          return false;
        }
      },
    [drillFields, currentSnapshot, filter]
  );

  const tagTableCols = useMemo(() => {
    const curDrillData = curData.filter(filterMethod({ excludeAJ: true }));
    const tarDrillData = tarData.filter(filterMethod({ excludeAJ: true }));
    const filteredLastYear = lastYearData.filter(filterMethod({ excludeAJ: true }));
    const filteredPlan = planData.filter(filterMethod({ excludeAJ: true }));

    const mergedCols = [
      ...Object.keys(groupBy([...curDrillData, ...tarDrillData, ...filteredLastYear, ...filteredPlan], curDrillFieldKey)),
    ];
    // const sortedCols = mergedCols.sort((a, b) => segmentSortSeqMap[a] - segmentSortSeqMap[b]);
    let sortedCols = mergedCols;
    switch (curDrillFieldKey) {
      case 'segment':
        sortedCols.sort((a, b) => segmentSortSeqMap[a] - segmentSortSeqMap[b]);
        break;
      case 'businessUnit':
        const map = buSortMap[drillFields[0].value];
        if (drillFields[0].value[0] === '原子力') {
          sortedCols = sortedCols.filter((dv) => dv !== '国内');
        }
        sortedCols.sort((a, b) => map[a] - map[b]);
        break;
      case 'model':
        sortedCols = orderBy(
          sortedCols,
          [
            (column) => {
              if (column === '白地') {
                return Infinity;
              }
              return sortedCols.indexOf(column);
            },
          ],
          ['asc']
        );
        break;
      case 'customer':
        sortedCols = orderBy(
          sortedCols,
          [
            (column) => {
              const total = sumBy(
                curDrillData.filter((item) => item[curDrillFieldKey] === column),
                'salesAmount'
              );
              if (isNil(total) || Number.isNaN(total)) {
                return -Infinity;
              }
              return total;
            },
          ],
          ['desc']
        );
        break;
      default:
        break;
    }
    const cols: any[] = sortedCols.map((cf) => {
      return {
        dataIndex: cf,
        title:
          drillFields.length < 4 ? (
            <div
              title={cf}
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                overflowX: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              onClick={() => {
                // setDrillInfos((prev) => [
                //   ...prev,
                //   {
                //     drillIndex: prev.length,
                //     drillField: cf,
                //   },
                // ]);
                setDrillFields((prev) => [
                  ...prev,
                  {
                    fieldKey: curDrillFieldKey,
                    value: [cf],
                  },
                ]);
              }}
            >
              {cf}
            </div>
          ) : (
            <div title={cf} style={{ overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cf}
            </div>
          ),
        render: (val) => {
          return <div style={{ textAlign: 'right' }}>{kpiValue(KPI_CODE.SALES_AMOUNT, cf === '' ? val[cf] : val)}</div>;
        },
      };
    });
    cols.push({
      title: '調整',
      dataIndex: '調整',
      render: (val) => {
        return <div style={{ textAlign: 'right' }}>{kpiValue(KPI_CODE.SALES_AMOUNT, val)}</div>;
      },
    });
    cols.push({
      title: '合計差分',
      dataIndex: '差分',
      fixed: 'right',
      render: (val) => {
        return <div style={{ textAlign: 'right' }}>{kpiValue(KPI_CODE.SALES_AMOUNT, val, true)}</div>;
      },
    });
    cols.unshift({
      title: '合計',
      dataIndex: 'total',
      // width: 100,
      fixed: 'left',
      render: (val) => {
        return <div style={{ textAlign: 'right' }}>{kpiValue(KPI_CODE.SALES_AMOUNT, val)}</div>;
      },
    });

    if (cols.length > 8) {
      cols.forEach((c) => {
        c.width = 120;
      });
    }
    cols.unshift({
      title: `${drillLabelsMap[curDrillFieldKey]}${kpiUnitLabel(KPI_CODE.SALES_AMOUNT)}`,
      dataIndex: 'type',
      align: 'left',
      width: 160,
      fixed: 'left',
    });
    return cols;
  }, [curData, tarData, planData, lastYearData, drillFields, filter]);

  const tagTableData = useMemo(() => {
    const { evaluationPeriod } = filter;
    const types = [`今月断面(${getMonthLabel(currentSnapshot)})`, '予算', `比較断面(${getMonthLabel(filter.targetSnapshot)})`, '前年同月'];
    let curAdjust = null;
    const curTransformed = curData.filter(filterMethod({})).reduce((pr, c) => {
      if (c[curDrillFieldKey] === '調整') {
        curAdjust += c.salesAmount;
      } else {
        if (pr[c[curDrillFieldKey]]) {
          pr[c[curDrillFieldKey]] += Number(c.salesAmount);
        } else {
          pr[c[curDrillFieldKey]] = Number(c.salesAmount);
        }
      }
      return pr;
    }, {});
    curTransformed['調整'] = curAdjust;
    curTransformed['total'] = Object.values<number>(curTransformed).reduce((pr, v) => pr + Number(v ?? null), 0);
    if (drillFields.length === 0) {
      if (balanceKeyMap[evaluationPeriod] === 'full') {
        curTransformed['調整'] = overallCur[0]?.salesActuals + overallCur[0]?.salesForecast - curTransformed['total'];
      } else {
        curTransformed['調整'] = overallCur[0]?.[balanceKeyMap[evaluationPeriod]] - curTransformed['total'];
      }
    }
    curTransformed['差分'] = null;

    let tarAdjust = null;
    const tarTransformed = tarData.filter(filterMethod({})).reduce((pr, c) => {
      if (c[curDrillFieldKey] === '調整') {
        tarAdjust += c.salesAmount;
      } else {
        if (pr[c[curDrillFieldKey]]) {
          pr[c[curDrillFieldKey]] += Number(c.salesAmount);
        } else {
          pr[c[curDrillFieldKey]] = Number(c.salesAmount);
        }
      }

      return pr;
    }, {});
    tarTransformed['調整'] = tarAdjust;
    tarTransformed['total'] = Object.values<number>(tarTransformed).reduce((pr, v) => pr + Number(v ?? null), 0);
    if (drillFields.length === 0) {
      if (balanceKeyMap[evaluationPeriod] === 'full') {
        tarTransformed['調整'] = overallTar[0]?.salesActuals + overallTar[0]?.salesForecast - tarTransformed['total'];
      } else {
        tarTransformed['調整'] = overallTar[0]?.[balanceKeyMap[evaluationPeriod]] - tarTransformed['total'];
      }
    }
    tarTransformed['差分'] = curTransformed['total'] - tarTransformed['total'];

    let lyAdjust = null;
    const lyTransformed = lastYearData.filter(filterMethod({})).reduce((pr, c) => {
      if (c[curDrillFieldKey] === '調整') {
        lyAdjust += c.salesAmount;
      } else {
        if (pr[c[curDrillFieldKey]]) {
          pr[c[curDrillFieldKey]] += Number(c.salesAmount);
        } else {
          pr[c[curDrillFieldKey]] = Number(c.salesAmount);
        }
      }
      return pr;
    }, {});
    lyTransformed['調整'] = lyAdjust;
    lyTransformed['total'] = Object.values<number>(lyTransformed).reduce((pr, v) => pr + Number(v ?? null), 0);
    if (drillFields.length === 0) {
      if (balanceKeyMap[evaluationPeriod] === 'full') {
        lyTransformed['調整'] = overallLY[0]?.salesActuals + overallLY[0]?.salesForecast - lyTransformed['total'];
      } else {
        lyTransformed['調整'] = overallLY[0]?.[balanceKeyMap[evaluationPeriod]] - lyTransformed['total'];
      }
    }
    lyTransformed['差分'] = curTransformed['total'] - lyTransformed['total'];

    let planAdjust = null;
    const planTransformed = planData.filter(filterMethod({})).reduce((pr, c) => {
      if (c[curDrillFieldKey] === '調整') {
        planAdjust += c.salesAmount;
      } else {
        if (c[curDrillFieldKey]) {
          if (pr[c[curDrillFieldKey]]) {
            pr[c[curDrillFieldKey]] += Number(c.salesAmount);
          } else {
            pr[c[curDrillFieldKey]] = Number(c.salesAmount);
          }
        }
      }
      return pr;
    }, {});
    planTransformed['調整'] = planAdjust;
    planTransformed['total'] = Object.values<number>(planTransformed).reduce((pr, v) => pr + Number(v ?? null), 0);
    if (drillFields.length === 0) {
      if (balanceKeyMap[evaluationPeriod] === 'full') {
        planTransformed['調整'] = overallCur[0]?.salesPlanActuals + overallCur[0]?.salesPlanForecast - planTransformed['total'];
      } else {
        planTransformed['調整'] = overallCur[0]?.[balanceKeyMap[evaluationPeriod]] - planTransformed['total'];
      }
    }
    planTransformed['差分'] = curTransformed['total'] - planTransformed['total'];
    const transformedData = [curTransformed, planTransformed, tarTransformed, lyTransformed];
    types.forEach((t, i) => {
      transformedData[i].type = t;
    });
    return transformedData;
  }, [curData, tarData, planData, lastYearData, drillFields, filter, overallCur, overallTar, overallLY]);

  const projectTableData = useMemo(() => {
    const filteredProjectData = projectData.filter(filterMethod({}));

    return filteredProjectData;
  }, [projectData, drillFields, filter]);

  const searchInput = useRef(null);
  const getColumnSearchProps = (dataIndex: string, title: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, close }) => (
        <div style={{ padding: 8, width: 320 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            style={{ width: '100%' }}
            allowClear
            ref={searchInput}
            placeholder={`${title}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
            }}
          />
          <Flex style={{ marginTop: 6 }} justify="flex-end">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                confirm();
              }}
            >
              Search
            </Button>
          </Flex>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? color.primaryColor : undefined }} />,
      filterDropdownProps: {
        onOpenChange(open) {
          if (open) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
      },
      onFilter: (value, record) => {
        return record[dataIndex].includes(value);
      },
    };
  };

  const projectTableCols = useMemo(() => {
    const segFilters = projectTableData.reduce((pr, pt) => (pr.includes(pt.segment) ? pr : pr.concat(pt.segment)), []);
    const buFilters = projectTableData.reduce((pr, pt) => (pr.includes(pt.businessUnit) ? pr : pr.concat(pt.businessUnit)), []);
    const salesDepFilters = projectTableData.reduce((pr, pt) => (pr.includes(pt.model) ? pr : pr.concat(pt.model)), []);
    const basisFilters = projectTableData.reduce((pr, pt) => (pr.includes(pt.salesBasis) ? pr : pr.concat(pt.salesBasis)), []);
    const departmentFilters = projectTableData.reduce(
      (pr, pt) => (pr.includes(pt.salesDepartment) ? pr : pr.concat(pt.salesDepartment)),
      []
    );
    const orderStatusFilters = projectTableData.reduce((pr, pt) => (pr.includes(pt.orderStatus) ? pr : pr.concat(pt.orderStatus)), []);
    return [
      {
        title: 'セグメント',
        dataIndex: 'segment',
        fixed: 'left',
        width: 140,
        filters: segFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.segment.includes(value);
          return record.segment === value;
        },
      },
      {
        title: 'BU',
        dataIndex: 'businessUnit',
        fixed: 'left',
        width: 120,
        filters: buFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.businessUnit === value;
        },
        render: (val) => {
          return (
            <Tooltip title={<div style={{ fontSize: fontSize.table }}>{val}</div>}>
              <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
            </Tooltip>
          );
        },
      },
      {
        title: '案件名',
        dataIndex: 'projectName',
        fixed: 'left',
        width: 180,
        render: (val) => {
          return (
            <Tooltip title={<div style={{ fontSize: fontSize.table }}>{val}</div>}>
              <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
            </Tooltip>
          );
        },
        ...getColumnSearchProps('projectName', '案件名'),
      },
      {
        title: '売上基準',
        dataIndex: 'salesBasis',
        width: 120,
        filters: basisFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          return record.salesBasis === value;
        },
      },
      {
        title: '機種',
        width: 180,
        dataIndex: 'model',
        filters: salesDepFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.model.includes(value);
          return record.model === value;
        },
        render: (val) => (
          <Tooltip title={<div style={{ fontSize: fontSize.table }}>{val}</div>}>
            <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
          </Tooltip>
        ),
      },
      // {
      //   title: '顧客',
      //   dataIndex: 'customer',
      //   width: 200,
      //   render: (val) => (
      //     <Tooltip title={<div style={{ fontSize: fontSize.table }}>{val}</div>}>
      //       <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
      //     </Tooltip>
      //   ),
      //   ...getColumnSearchProps('customer', '顧客'),
      // },
      {
        title: '設置場所名',
        width: 180,
        dataIndex: 'location',
        // filters: salesSecFilters.map((s) => ({ text: s, value: s })),
        // onFilter: (value, record) => {
        //   // return record.location.includes(value);
        //   return record.location === value;
        // },
        render: (val) => (
          <Tooltip title={<div style={{ fontSize: fontSize.table }}>{val}</div>}>
            <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
          </Tooltip>
        ),
        ...getColumnSearchProps('location', '設置場所'),
      },
      {
        title: '営業部',
        dataIndex: 'salesDepartment',
        width: 120,
        filters: departmentFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          return record.salesDepartment === value;
        },
      },
      {
        title: '受注区分',
        dataIndex: 'orderStatus',
        width: 140,
        filters: orderStatusFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          return record.orderStatus === value;
        },
      },
      {
        title: <div>今回</div>,
        dataIndex: 'cur',
        width: 100,
        sorter: (a, b) => a.cur - b.cur,
        render: (val) => {
          return (
            <div style={{ textAlign: 'right' }}>
              {kpiValue(KPI_CODE.SALES_AMOUNT, val, false, {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 1000000,
                  unitLabel: '百万',
                  precision: 0,
                },
              })}
            </div>
          );
        },
      },
      {
        title: <div>前回</div>,
        dataIndex: 'prev',
        width: 100,
        sorter: (a, b) => a.prev - b.prev,
        render: (val) => {
          return (
            <div style={{ textAlign: 'right' }}>
              {kpiValue(KPI_CODE.SALES_AMOUNT, val, false, {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 1000000,
                  unitLabel: '百万',
                  precision: 0,
                },
              })}
            </div>
          );
        },
      },
      {
        title: <div>変動額</div>,
        dataIndex: 'diff',
        width: 120,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => a.diff - b.diff,
        render: (val) => {
          const value = kpiValue(KPI_CODE.SALES_AMOUNT, val, true, {
            [KPI_CODE.SALES_AMOUNT]: {
              unitValue: 1000000,
              unitLabel: '百万',
              precision: 0,
            },
          });
          return <div style={{ textAlign: 'right' }}>{value}</div>;
        },
      },
      {
        title: '変動割合',
        dataIndex: 'diffRate',
        width: 120,
        sorter: (a, b) => a.diffRate - b.diffRate,
        render: (val) => {
          return <div style={{ textAlign: 'right' }}>{kpiValue(KPI_CODE.DIFF_RATE, val, true)}</div>;
        },
      },
    ];
  }, [projectTableData, fontSize]);

  return (
    <>
      <TicketHeader
        submit={getData}
        form={form}
        loading={loading}
        drillDownRule={drillDownRule}
        setDrillDownRule={setDrillDownRule}
        defaultDrillDownRule={defaultDrillDownRule}
      />
      <Splitter layout="vertical" style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
        <Splitter.Panel resizable={false} collapsible={{ start: true, end: true, showCollapsibleIcon: true }} defaultSize={233}>
          <div className="tag-table" style={{ marginBottom: 4 }}>
            <Table
              style={{ maxHeight: 360 }}
              size="small"
              bordered
              className="border-right-table"
              loading={loading}
              columns={tagTableCols}
              dataSource={tagTableData}
              pagination={false}
              scroll={{ x: tagTableCols.reduce((pr, c) => pr + Number(c.width ?? null), 0) }}
            />
          </div>
        </Splitter.Panel>
        <Splitter.Panel resizable={false}>
          <Card
            title="案件明細"
            styles={{
              body: { padding: 0 },
              header: { color: '#555', fontWeight: 'bold', fontSize: fontSize.tableTitle, padding: '0 12px' },
            }}
            classNames={{ header: 'list-card-title' }}
            extra={
              <span style={{ fontSize: fontSize.tag }}>
                単位：
                {kpiUnitLabel(
                  KPI_CODE.SALES_AMOUNT,
                  {
                    [KPI_CODE.SALES_AMOUNT]: {
                      unitValue: 1000000,
                      unitLabel: '百万円',
                    },
                  },
                  false
                )}
              </span>
            }
          >
            <div className="project-table" style={{ minHeight: selectorHeight4Table + 30, backgroundColor: 'white' }}>
              <Table
                size="small"
                style={{ marginBottom: 6 }}
                loading={loading}
                columns={projectTableCols as any[]}
                dataSource={projectTableData}
                pagination={false}
                virtual
                scroll={{ y: selectorHeight4Table - 10 }}
                rowClassName={(record: any, index) => {
                  if (record.projectName === '調整') {
                    return 'row-grey';
                  }
                  return '';
                }}
              />
            </div>
          </Card>
        </Splitter.Panel>
      </Splitter>
    </>
  );
};

export default TicketPage;
